import React, { useState, useEffect } from "react";
import axios from "axios";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../../config/api";

const AccountATMFormModal = ({ atms, setAtms, editingAtm, onClose }) => {
  const [formData, setFormData] = useState({
    acc: "", // This will now store the selected account ID
    atm_number: "",
    expiry: "",
  });

  const [accounts, setAccounts] = useState([]); // To store fetched account details

  // Fetch account details from the API
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axios.get(
          getApiUrl("/PartnerFiles/account-details/"),
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        setAccounts(response.data.data); // Store the fetched accounts
      } catch (error) {
        console.error("Error fetching account details:", error);
      }
    };
    fetchAccountDetails();
  }, []);

  // Update formData whenever editingAtm changes
  useEffect(() => {
    if (editingAtm) {
      setFormData({
        acc: editingAtm.acc || "",
        atm_number: editingAtm.atm_number || "",
        expiry: editingAtm.expiry || "",
      });
    } else {
      setFormData({ acc: "", atm_number: "", expiry: "" });
    }
  }, [editingAtm]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const accessToken = localStorage.getItem("access_token");
      if (editingAtm) {
        // Update existing ATM
        await axios.put(
          getApiUrl(`/PartnerFiles/api/atms/${editingAtm.id}/`),
          formData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setAtms(
          atms.map((atm) =>
            atm.id === editingAtm.id ? { ...atm, ...formData } : atm
          )
        );
      } else {
        // Create new ATM
        const response = await axios.post(
          getApiUrl("/PartnerFiles/api/atms/create/"),
          formData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setAtms([...atms, response.data]);
      }
      onClose(); // Close modal after submission
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 flex justify-center items-center z-50"
      id="marketingmodalopen"
      style={{ width: "100vw", height: "100vh" }}
    >
      {/* Background overlay */}
      <div
        className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"
        style={{
          width: "100vw",
          height: "150%",
          background: "black",
          backgroundSize: "200% 200vh",
          overflowY: "hidden",
          paddingBottom: "150px",
        }}
      ></div>

      {/* Modal content */}
      <div
        className="relative bg-white rounded-lg shadow-lg"
        style={{
          height: "95vh", // Set height to allow scrolling
          overflowY: "auto", // Enable scrolling inside modal
        }}
      >
        <div
          className="overflow-auto max-h-[80vh]"
          style={{ padding: "100px", paddingTop: "20px" }}
        >
          <h2 className="text-center text-blue-700 mb-5">
            {editingAtm ? "Edit ATM" : "Add ATM"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="bg-blue-500 text-white px-3 py-2 rounded hover:bg-red-700 transition-colors"
            style={{ position: "absolute", top: "20px", right: "20px" }}
          >
            X
          </button>

          {/* Form Fields */}
          <form onSubmit={handleSubmit}>
            {/* Account Dropdown */}
            <div className="mb-4">
              <label
                htmlFor="acc"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Select Account:
              </label>
              <select
                id="acc"
                name="acc"
                value={formData.acc}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select an account --</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.acc_name
                      ? `${account.acc_name} (${account.acc_number})`
                      : `Account ID: ${account.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* ATM Number */}
            <div className="mb-4">
              <label
                htmlFor="atm_number"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                ATM Number:
              </label>
              <input
                type="text"
                id="atm_number"
                name="atm_number"
                value={formData.atm_number}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Expiry Date */}
            <div className="mb-4">
              <label
                htmlFor="expiry"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Expiry Date:
              </label>
              <input
                type="date"
                id="expiry"
                name="expiry"
                value={formData.expiry}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-200"
              >
                {editingAtm ? "Update" : "Create"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountATMFormModal;
